/**
 * React Query Hooks fuer Image Intelligence
 *
 * Hooks fuer Analyse, Kontingent und Packshot-Zuweisung.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getAnalysisEstimate,
  startAnalysis,
  getAnalysisStatus,
  cancelAnalysis,
  getAnalysisQuota,
  getPackshotStatuses,
  setPackshot,
  bulkSetPackshot,
} from '@/lib/api/image-intelligence'
import type { ProductPackshotStatus } from '@/lib/api/image-intelligence'
import { getAIReferenceSummary } from '@/lib/api/products'
import type { AIRefSummaryEntry } from '@/lib/api/products'

// =============================================================================
// QUERY HOOKS
// =============================================================================

export function useAnalysisEstimate(scope?: string, productIds?: string[]) {
  return useQuery({
    queryKey: ['image-intelligence', 'estimate', scope, productIds],
    queryFn: () => getAnalysisEstimate(scope, productIds),
    staleTime: 30_000,
  })
}

export function useAnalysisStatus(runId: string | null) {
  return useQuery({
    queryKey: ['image-intelligence', 'status', runId],
    queryFn: () => getAnalysisStatus(runId!),
    enabled: !!runId,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      if (status === 'pending' || status === 'running') return 2000
      return false
    },
  })
}

export function useAnalysisQuota() {
  return useQuery({
    queryKey: ['image-intelligence', 'quota'],
    queryFn: getAnalysisQuota,
    staleTime: 60_000,
  })
}

// Stabile select-Funktionen außerhalb der Hooks — verhindert neue Map-Instanz bei jedem Render
const toPackshotStatusMap = (data: Record<string, ProductPackshotStatus>): Map<string, ProductPackshotStatus> =>
  new Map(Object.entries(data))

const toAIRefSummaryMap = (data: Record<string, AIRefSummaryEntry>): Map<string, AIRefSummaryEntry> =>
  new Map(Object.entries(data))

export function usePackshotStatuses(productIds: string[], channelId?: string) {
  // Join zu String für stabilen queryKey (Array-Referenz wechselt bei jedem Render)
  const idsKey = productIds.join(',')
  return useQuery({
    queryKey: ['image-intelligence', 'packshot-statuses', idsKey, channelId],
    queryFn: () => getPackshotStatuses(productIds, channelId),
    enabled: productIds.length > 0,
    staleTime: 60_000,
    select: toPackshotStatusMap,
  })
}

export function useAIReferenceSummary(productIds: string[]) {
  const idsKey = productIds.join(',')
  return useQuery({
    queryKey: ['image-intelligence', 'ai-ref-summary', idsKey],
    queryFn: () => getAIReferenceSummary(productIds),
    enabled: productIds.length > 0,
    staleTime: 60_000,
    select: toAIRefSummaryMap,
  })
}

// =============================================================================
// MUTATION HOOKS
// =============================================================================

export function useStartAnalysis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ scope, autoAssignThreshold, productIds }: { scope?: string; autoAssignThreshold?: number; productIds?: string[] }) =>
      startAnalysis(scope, autoAssignThreshold, productIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['image-intelligence'] })
    },
  })
}

export function useCancelAnalysis() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (runId: string) => cancelAnalysis(runId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['image-intelligence'] })
      queryClient.invalidateQueries({ queryKey: ['readiness'] })
    },
  })
}

export function useSetPackshot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      productId,
      assetId,
      slot,
      channelId,
    }: {
      productId: string
      assetId: string
      slot?: number
      channelId?: string
    }) => setPackshot(productId, assetId, slot, channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['readiness'] })
      queryClient.invalidateQueries({ queryKey: ['image-intelligence'] })
    },
  })
}

export function useBulkSetPackshot() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      productIds,
      minScore,
      slot,
      channelId,
    }: {
      productIds: string[]
      minScore?: number
      slot?: number
      channelId?: string
    }) => bulkSetPackshot(productIds, minScore, slot, channelId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['readiness'] })
      queryClient.invalidateQueries({ queryKey: ['image-intelligence'] })
    },
  })
}
